import { UseFormSetValue } from "react-hook-form";
import { TextInput } from "../../components/shared/Inputs/TextInput";
import { useEffect, useMemo, useState } from "react";
import LoadingWheel from "../../components/shared/LoadingWheel";
import Modal from "../../layouts/Modal";
import {
  ArrowRightIcon,
  CheckIcon,
  EnvelopeIcon,
  ExclamationCircleIcon,
  PhoneIcon,
} from "@heroicons/react/24/outline";

import getAddress from "../../utils/getAddress";
import classNames from "../../utils/classNames";
import useAccountAddresses from "../../data/useAccountAddresses";
import useAccountContacts from "../../data/useAccountContacts";
import useUtilities from "../../data/useUtilities";
import useNotification from "../../components/notifications/useNotifications";
import { SearchAccount } from "../../components/accounts/SearchAccount";
import { IAccount } from "../../models/account/IAccount";
import { IAccountAddress } from "../../models/account/IAccountAddress";
import { IContact } from "../../models/Contact";
import SwitchInput from "../../components/shared/Inputs/SwitchInput";

const ADDRESS_FIELDS: {
  key: keyof Pick<
    IAccountAddress,
    | "postcode"
    | "line1"
    | "line2"
    | "line3"
    | "line4"
    | "locality"
    | "town"
    | "county"
  >;
  label: string;
}[] = [
  { key: "postcode", label: "Postcode" },
  { key: "line1", label: "Line 1" },
  { key: "line2", label: "Line 2" },
  { key: "line3", label: "Line 3" },
  { key: "line4", label: "Line 4" },
  { key: "locality", label: "Locality" },
  { key: "town", label: "Town" },
  { key: "county", label: "County" },
];

const norm = (v: unknown) =>
  v === null || v === undefined ? "" : String(v).trim().toLowerCase();

interface IBasketInvoiceInputsProps {
  register: any;
  setValue: UseFormSetValue<any>;
  watch: (name: string) => any;
  setSelectedAccount: (account: any) => void;
  selectedAccount?: IAccount | null;
  disabled?: boolean;
  showTaxOption?: boolean;
}

export default function BasketInvoiceInputs({ register, setValue, watch, setSelectedAccount, selectedAccount, disabled, showTaxOption = true }: IBasketInvoiceInputsProps) {
  const { accountAddresses } = useAccountAddresses(selectedAccount?.id);
  const { accountContacts } = useAccountContacts(selectedAccount?.id);

  const customerAddress = useMemo<IAccountAddress | null>(() => {
    const addresses = accountAddresses.data?.data ?? [];
    if (!selectedAccount) return null;
    if (selectedAccount.billingAddressId) {
      const billing = addresses.find((a) => a.id === selectedAccount.billingAddressId);
      if (billing) return billing;
    }
    if (selectedAccount.shippingAddressId) {
      const shipping = addresses.find((a) => a.id === selectedAccount.shippingAddressId);
      if (shipping) return shipping;
    }
    return addresses[0] ?? null;
  }, [accountAddresses.data, selectedAccount]);

  const primaryContact = useMemo<IContact | null>(() => {
    const contacts = accountContacts.data?.data ?? [];
    if (!selectedAccount || contacts.length === 0) return null;
    if (selectedAccount.primaryContactId) {
      const match = contacts.find((c) => c.id === selectedAccount.primaryContactId);
      if (match) return match;
    }
    return contacts[0] ?? null;
  }, [accountContacts.data, selectedAccount]);

  // Prefill email/mobile from the account's primary contact, but only if the
  // user hasn't already typed something. Re-runs whenever the resolved contact
  // changes (e.g. after switching accounts).
  useEffect(() => {
    if (!primaryContact) return;
    const currentEmail = (watch("email") ?? "").trim();
    if (!currentEmail && primaryContact.email) {
      setValue("email", primaryContact.email);
    }
    const currentMobile = (watch("mobile") ?? "").trim();
    const contactMobile =
      primaryContact.telephone1 || primaryContact.telephone2 || "";
    if (!currentMobile && contactMobile) {
      setValue("mobile", contactMobile);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primaryContact?.id]);

  return (
    <div className="flex flex-col gap-4 mt-4">

      <SearchAccount
        onAccountSelect={setSelectedAccount}
        label="Search Account"
        selectedAccount={selectedAccount}
        disabled={disabled}
      />

      <CustomerContactDetails
        register={register}
        watch={watch}
        primaryContact={primaryContact}
        contactsLoading={accountContacts.isLoading && !!selectedAccount}
        disabled={disabled}
      />

      <CustomerAndShippingAddress
        selectedAccount={selectedAccount}
        customerAddress={customerAddress}
        accountAddressesLoading={accountAddresses.isLoading}
        register={register}
        setValue={setValue}
        watch={watch}
        disabled={disabled}
      />

      <TextInput richText label={"Notes"} register={register} registerName={"notes"} disabled={disabled} />

      {showTaxOption && (
        <SwitchInput
          label="Add Tax to Total"
          register={register}
          registerName={"addTaxToTotal"}
          setValue={setValue}
          watch={watch}
        />
      )}
    </div>
  )
}

interface CustomerAndShippingAddressProps {
  selectedAccount?: IAccount | null;
  customerAddress: IAccountAddress | null;
  accountAddressesLoading: boolean;
  register: any;
  setValue: UseFormSetValue<any>;
  watch: (name: string) => any;
  disabled?: boolean;
}

function CustomerAndShippingAddress({
  selectedAccount,
  customerAddress,
  accountAddressesLoading,
  register,
  setValue,
  watch,
  disabled,
}: CustomerAndShippingAddressProps) {
  const { getAddresses } = useUtilities();
  const { addNotification } = useNotification();
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupOpen, setLookupOpen] = useState(false);
  const [lookupResults, setLookupResults] = useState<any[]>([]);

  const copyToShipping = () => {
    if (!customerAddress) return;
    ADDRESS_FIELDS.forEach(({ key }) => {
      setValue(key, customerAddress[key] ?? "");
    });
  };

  const shippingValues = ADDRESS_FIELDS.reduce(
    (acc, { key }) => {
      acc[key] = watch(key) ?? "";
      return acc;
    },
    {} as Record<string, string>
  );

  const fieldDiffers = (key: string) => {
    if (!customerAddress) return false;
    const c = norm((customerAddress as any)[key]);
    const s = norm(shippingValues[key]);
    if (!c && !s) return false;
    return c !== s;
  };

  const fieldMatches = (key: string) => {
    if (!customerAddress) return false;
    const c = norm((customerAddress as any)[key]);
    const s = norm(shippingValues[key]);
    return !!c && !!s && c === s;
  };

  const anyDifferent = ADDRESS_FIELDS.some((f) => fieldDiffers(f.key));

  const handleLookup = () => {
    const postcode = (watch("postcode") ?? "").trim();
    if (!postcode) {
      addNotification({
        variant: "error",
        primaryText: "Enter a postcode to look up",
      });
      return;
    }
    setLookupLoading(true);
    getAddresses.mutate(postcode, {
      onSuccess: (data: any) => {
        const list: any[] = data?.data ?? [];
        if (list.length === 0) {
          addNotification({
            variant: "error",
            primaryText: "No addresses found",
          });
          return;
        }
        setLookupResults(
          [...list].sort((a, b) =>
            a.line1
              ? a.line1.localeCompare(b.line1, "en", { numeric: true })
              : 1
          )
        );
        setLookupOpen(true);
      },
      onError: () => {
        addNotification({
          variant: "error",
          primaryText: "Could not get addresses",
        });
      },
      onSettled: () => setLookupLoading(false),
    });
  };

  const applyLookupAddress = (addr: any) => {
    setValue("postcode", addr.postcode ?? watch("postcode") ?? "");
    setValue("line1", addr.line1 ?? "");
    setValue("line2", addr.line2 ?? "");
    setValue("line3", addr.line3 ?? "");
    setValue("line4", addr.line4 ?? "");
    setValue("locality", addr.locality ?? "");
    setValue(
      "town",
      addr.town ?? addr.city ?? addr.county ?? ""
    );
    setValue("county", addr.county ?? "");
    setLookupOpen(false);
  };

  return (
    <>
      <Modal open={lookupOpen} setOpen={setLookupOpen} width="max-w-4xl">
        <div className="flex max-h-[80vh] w-full flex-col overflow-auto">
          {lookupResults.map((address, i) => (
            <div
              key={i}
              role="button"
              tabIndex={0}
              onClick={() => applyLookupAddress(address)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  applyLookupAddress(address);
                }
              }}
              className="flex w-full cursor-pointer flex-col border-b border-gray-200 p-4 hover:bg-gray-100"
            >
              {getAddress(address)}
            </div>
          ))}
        </div>
      </Modal>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Customer (read-only) */}
        <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-gray-700">
              Customer Address
            </p>
            {customerAddress && (
              <button
                type="button"
                onClick={copyToShipping}
                disabled={disabled}
                className="inline-flex items-center gap-1 text-xs font-medium text-primary-600 hover:text-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Copy to shipping
                <ArrowRightIcon className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            )}
          </div>

          {!selectedAccount && (
            <p className="mt-2 text-xs italic text-gray-500">
              Select an account to see the customer address.
            </p>
          )}
          {selectedAccount && accountAddressesLoading && (
            <p className="mt-2 text-xs italic text-gray-500">
              Loading address…
            </p>
          )}
          {selectedAccount && !accountAddressesLoading && !customerAddress && (
            <p className="mt-2 text-xs italic text-gray-500">
              No address on this account.
            </p>
          )}

          {customerAddress && (
            <dl className="mt-3 flex flex-col gap-2">
              {ADDRESS_FIELDS.map(({ key, label }) => {
                const value = (customerAddress as any)[key] ?? "";
                const differs = fieldDiffers(key);
                const matches = fieldMatches(key);
                return (
                  <AddressFieldRow
                    key={key}
                    label={label}
                    differs={differs}
                    matches={matches}
                  >
                    <span
                      className={classNames(
                        "block min-h-[2.25rem] rounded border px-2 py-1.5 text-sm",
                        differs
                          ? "border-rose-200 bg-rose-50 text-rose-800"
                          : matches
                          ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                          : "border-gray-200 bg-white text-gray-700"
                      )}
                    >
                      {value || (
                        <span className="text-gray-400">—</span>
                      )}
                    </span>
                  </AddressFieldRow>
                );
              })}
            </dl>
          )}
        </div>

        {/* Shipping (editable, mirrored) */}
        <div className="rounded-md border border-gray-200 p-3">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-gray-700">
              Shipping Address
            </p>
            {customerAddress && anyDifferent && (
              <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-700 ring-1 ring-amber-200">
                Different from customer
              </span>
            )}
            {customerAddress && !anyDifferent && (
              <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-medium text-emerald-700 ring-1 ring-emerald-200">
                Matches customer
              </span>
            )}
          </div>

          <dl className="mt-3 flex flex-col gap-2">
            {ADDRESS_FIELDS.map(({ key, label }) => {
              const differs = fieldDiffers(key);
              const matches = fieldMatches(key);
              return (
                <AddressFieldRow
                  key={key}
                  label={label}
                  differs={differs}
                  matches={matches}
                >
                  {key === "postcode" ? (
                    <div className="flex items-stretch gap-2">
                      <input
                        type="text"
                        {...register(key)}
                        disabled={disabled}
                        className="h-9 w-full rounded border border-gray-300 px-2 py-1.5 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:cursor-not-allowed disabled:bg-gray-50"
                      />
                      {lookupLoading ? (
                        <div className="flex items-center px-2">
                          <LoadingWheel />
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={handleLookup}
                          disabled={disabled}
                          className="h-9 shrink-0 rounded bg-primary-600 px-3 text-xs font-semibold text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          Lookup
                        </button>
                      )}
                    </div>
                  ) : (
                    <input
                      type="text"
                      {...register(key)}
                      disabled={disabled}
                      className="h-9 w-full rounded border border-gray-300 px-2 py-1.5 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:cursor-not-allowed disabled:bg-gray-50"
                    />
                  )}
                </AddressFieldRow>
              );
            })}
          </dl>
        </div>
      </div>
    </>
  );
}

interface CustomerContactDetailsProps {
  register: any;
  watch: (name: string) => any;
  primaryContact: IContact | null;
  contactsLoading: boolean;
  disabled?: boolean;
}

function CustomerContactDetails({
  register,
  watch,
  primaryContact,
  contactsLoading,
  disabled,
}: CustomerContactDetailsProps) {
  const currentEmail = (watch("email") ?? "").trim();
  const currentMobile = (watch("mobile") ?? "").trim();

  const contactEmail = (primaryContact?.email ?? "").trim();
  const contactMobile = (
    primaryContact?.telephone1 ||
    primaryContact?.telephone2 ||
    ""
  ).trim();

  const emailMatchesContact =
    !!contactEmail && norm(currentEmail) === norm(contactEmail);
  const mobileMatchesContact =
    !!contactMobile && norm(currentMobile) === norm(contactMobile);

  return (
    <div className="rounded-md border border-gray-200 p-3">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-gray-700">Customer Contact</p>
        {primaryContact && (
          <span className="text-[11px] text-gray-500">
            From{" "}
            <span className="font-medium text-gray-700">
              {primaryContact.fullname || "primary contact"}
            </span>
          </span>
        )}
        {!primaryContact && contactsLoading && (
          <span className="text-[11px] italic text-gray-500">
            Loading contact…
          </span>
        )}
      </div>

      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="flex flex-col gap-1">
          <label
            htmlFor="basket-customer-email"
            className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-gray-500"
          >
            <EnvelopeIcon className="h-3.5 w-3.5" aria-hidden="true" />
            Email
            {emailMatchesContact && (
              <CheckIcon
                className="h-3.5 w-3.5 text-emerald-500"
                aria-hidden="true"
              />
            )}
          </label>
          <input
            id="basket-customer-email"
            type="email"
            placeholder="customer@example.com"
            autoComplete="email"
            {...register("email")}
            disabled={disabled}
            className="h-9 w-full rounded border border-gray-300 px-2 py-1.5 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:cursor-not-allowed disabled:bg-gray-50"
          />
        </div>

        <div className="flex flex-col gap-1">
          <label
            htmlFor="basket-customer-mobile"
            className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-gray-500"
          >
            <PhoneIcon className="h-3.5 w-3.5" aria-hidden="true" />
            Mobile
            {mobileMatchesContact && (
              <CheckIcon
                className="h-3.5 w-3.5 text-emerald-500"
                aria-hidden="true"
              />
            )}
          </label>
          <input
            id="basket-customer-mobile"
            type="tel"
            placeholder="07000 000 000"
            autoComplete="tel"
            {...register("mobile")}
            disabled={disabled}
            className="h-9 w-full rounded border border-gray-300 px-2 py-1.5 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:cursor-not-allowed disabled:bg-gray-50"
          />
        </div>
      </div>
    </div>
  );
}

function AddressFieldRow({
  label,
  differs,
  matches,
  children,
}: {
  label: string;
  differs: boolean;
  matches: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="grid grid-cols-[6rem,1fr] items-start gap-2">
      <div className="flex h-9 items-center gap-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
        <span>{label}</span>
        {differs && (
          <ExclamationCircleIcon
            className="h-3.5 w-3.5 text-rose-500"
            aria-hidden="true"
          />
        )}
        {matches && (
          <CheckIcon
            className="h-3.5 w-3.5 text-emerald-500"
            aria-hidden="true"
          />
        )}
      </div>
      <div className="min-w-0">{children}</div>
    </div>
  );
}